/**
 * exact-search.js
 *
 * Workaround for Shopify's mandatory storefront semantic (AI) search, which
 * returns the whole "nearest-neighbour" cluster for any query. Because the
 * donor catalogue is ~hundreds of near-identical profiles, every unquoted
 * search lands in the middle of that cluster and returns essentially all
 * donors (verified: gibberish/typos/concepts all return ~all products).
 *
 * Quoting a term forces Shopify's keyword (non-semantic) path. Per-token
 * quoting gives keyword-AND matching:
 *   gottfried   -> "gottfried"          (semantic ALL -> exact 1)
 *   blue eyes   -> "blue" "eyes"        (semantic ALL -> keyword-AND)
 *
 * Strategy:
 *  1. On submit of any GET search form, rewrite the `q` value to per-token
 *     quotes so the results page uses keyword matching.
 *  2. On load, strip those quotes back out of any visible field / status text
 *     so the shopper still sees their original query.
 *
 * No Shopify setting can disable semantic search (mandatory since 2025-03-18),
 * so this lives in the theme. JS-off shoppers fall back to native (semantic)
 * search — acceptable graceful degradation.
 */
(function () {
  'use strict';

  // Wrap each whitespace-separated token in quotes; strip any existing quotes
  // first so re-submits stay idempotent. "" -> "".
  function toExact(raw) {
    var tokens = String(raw).replace(/"/g, ' ').trim().split(/\s+/).filter(Boolean);
    return tokens.map(function (t) { return '"' + t + '"'; }).join(' ');
  }

  // Unwrap "token" -> token (only single quoted tokens; leaves other text alone).
  function toDisplay(value) {
    return String(value).replace(/"([^"\s]+)"/g, '$1');
  }

  function isSearchForm(form) {
    if (!form || (form.getAttribute('method') || 'get').toLowerCase() !== 'get') return false;
    var action = form.getAttribute('action') || '';
    return /\/search(\/|\?|$)/.test(action) && !!form.querySelector('input[name="q"]');
  }

  // 1. Transform the query as the form submits.
  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!isSearchForm(form)) return;

    // Leave it alone when predictive search will handle navigation itself
    // (empty query, or a suggestion is highlighted -> its own <a> navigates).
    if (form.querySelector('[aria-selected="true"] a')) return;

    var input = form.querySelector('input[name="q"]');
    var raw = input.value;
    if (!raw || !raw.trim()) return;

    input.value = toExact(raw);
  }, true); // capture: run before predictive-search's bubble-phase submit handler

  // 2. Restore the human-readable query for display after the page loads.
  function cleanupDisplay() {
    document.querySelectorAll('input[name="q"]').forEach(function (input) {
      if (input.value && input.value.indexOf('"') !== -1) {
        input.value = toDisplay(input.value);
      }
    });

    // "X results for "..."" / "no results" copy inside the search template.
    document.querySelectorAll('.template-search [role="status"]').forEach(function (el) {
      if (el.textContent.indexOf('"') !== -1) {
        el.textContent = toDisplay(el.textContent);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', cleanupDisplay);
  } else {
    cleanupDisplay();
  }
})();
